import { put, select } from 'redux-saga/effects'
import { get, isEmpty } from 'lodash'
import moment from 'moment'

import { defaultAttributes } from './attributes'

import { clearPromotionsOnOrder } from '../../actions/promotion'
import { getCurrentEventId } from '../../selectors/events'
import { getCurrentMenu, makeGetCurrentMenu } from '../../selectors/menus'
import { makeGetCartItems } from '../../selectors/cart'
import { makeGetRemoteOrderTotal } from '../../selectors/orderTotalRemote'
import { getActiveRevenueCenterId } from '../../selectors/revenueCenter'
import { getActiveAttendantId } from '../../selectors/attendant'

import { nextPosOrderNumber } from '../../utils/orderNumbers'
import { makeOrderLineItems } from '../../utils/orderUtils'
import { DEFAULT_EXEMPTIONS } from '../../utils/orderTotalUtils'

import { resolveExemptionOverrideFlags } from '../../utils/orderTotalUtils'
import { makeLineItemData, resolveNextAmountInCents } from '../../utils/orderUtils'
import { stringFloatToCents } from '../../utils/formatters'
import { SIGNATURE_THRESHOLD_AMOUNT } from '../../constants'

const generateUuid = require('uuid/v4')
const getCartItems = makeGetCartItems()
const selectRemoteOrderTotal = makeGetRemoteOrderTotal()

const makeCreateOrderParams = ({
  amountInCents,
  customerPaymentId,
  employeeId,
  itemModels,
  orderNumber,
  paymentDisplay,
  paymentType,
  promotions,
  salesEventId,
  standMenuUuid,
  standUuid,
  state,
  submittedAt,
  taxExempt,
  exemptServiceFee,
  userAttributes,
  userName,
  uuid,
  userNotes
}) => {
  return {
    affiliations: [],
    clientCreatedAt: moment(submittedAt).toISOString(),
    employeeId,
    orderMenuItems: makeOrderLineItems(itemModels),
    orderNumber,
    payments: [
      {
        amountInCents,
        customerPaymentId,
        paymentType,
        shortDescription: paymentDisplay ?? ''
      }
    ],
    paymentType,
    promotions,
    salesEventId,
    standMenuUuid,
    standUuid,
    state,
    taxExempt,
    includeServiceCharges: !exemptServiceFee,
    exemptServiceFee, // Keeping just in case.
    webOrder: true,
    userAttributes,
    userName,
    uuid,
    userNotes
  }
}

function* makeLineItemAttributes(args = {}) {
  let lineItems = args.lineItems
  if (!lineItems) lineItems = yield select(getCartItems)

  const { amountInCents, itemModels } = makeLineItemData(lineItems)

  return { amountInCents, itemModels }
}

function* makeMenuAndCustomerAttributes() {
  const { menuSubtype, standMenuUuid, standUuid, isNoTipMenu } = yield menuAndRvcData()
  const salesEventId = yield select(getCurrentEventId)

  return {
    orderType: menuSubtype,
    salesEventId,
    standMenuUuid,
    standUuid,
    isNoTipMenu,
  }
}

function* makeOrderTotalAttributes(args = {}) {
  const exemptionOverrides = args?.exemptionOverrides ?? DEFAULT_EXEMPTIONS
  const remoteOrderTotal = yield select(selectRemoteOrderTotal)
  const currentOrderTotal = remoteOrderTotal?.totalAmountInCents ?? args.amountInCents
  const nextAmountInCents = resolveNextAmountInCents(currentOrderTotal, remoteOrderTotal, exemptionOverrides)
  let promotions = remoteOrderTotal?.promotions
  if (isEmpty(promotions)) {
    promotions = undefined
  }

  return {
    amountInCents: nextAmountInCents,
    exemptionOverrides,
    promotions,
    remoteOrderTotal
  }
}

function* menuAndRvcData() {
  const menu = yield select(getCurrentMenu)
  const standMenuUuid = get(menu, 'uuid')
  const menuSubtype = get(menu, 'subtype')
  const isNoTipMenu = isEmpty(menu?.tipSuggestions)

  let standUuid = yield select(getActiveRevenueCenterId)

  return { menuSubtype, standMenuUuid, standUuid, isNoTipMenu }
}

export function* createOrder(args = {}) {
  const { options, ...payloadArgs } = args
  const orderNumber = get(payloadArgs, 'orderNumber', nextPosOrderNumber())
  const menu = yield select(getCurrentMenu)
  const isNoTipMenu = isEmpty(menu.tipSuggestions)
  
  const lineItemAttrs = yield makeLineItemAttributes(payloadArgs)
  const menuAndCustomerAttrs = yield makeMenuAndCustomerAttributes()
  const orderTotalAttrs = yield makeOrderTotalAttributes({ ...payloadArgs, amountInCents: lineItemAttrs.amountInCents })
  const remoteOrderTotal = orderTotalAttrs.remoteOrderTotal
  const uuid = payloadArgs?.uuid ?? remoteOrderTotal?.uuid ?? generateUuid()
  const exemptionOverrideFlags = resolveExemptionOverrideFlags(orderTotalAttrs)
  let amountInCents = orderTotalAttrs.amountInCents
  const signatureRequired = SIGNATURE_THRESHOLD_AMOUNT === 0 || (SIGNATURE_THRESHOLD_AMOUNT && amountInCents >= SIGNATURE_THRESHOLD_AMOUNT)

  if (options?.useAuthedAmountInCents && payloadArgs?.cardReaderData?.amount !== undefined) {
    amountInCents = stringFloatToCents(payloadArgs.cardReaderData.amount)
  }

  const employeeId = yield select(getActiveAttendantId)
  yield put(clearPromotionsOnOrder())

  const combinedOverrideAttributes = {
    ...payloadArgs,
    ...lineItemAttrs,
    ...menuAndCustomerAttrs,
    ...orderTotalAttrs,
    ...exemptionOverrideFlags,
    amountInCents,
    orderNumber,
    uuid,
    employeeId,
    signatureRequired,
    isNoTipMenu
  }

  return {
    ...defaultAttributes,
    ...combinedOverrideAttributes,
    createOrderParams: makeCreateOrderParams(combinedOverrideAttributes),
    remoteOrderTotal
  }
}

export default createOrder
